import { useState } from "react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import { CircularProgress } from "@/components/CircularProgress/CircularProgress";
import { showSnackbar } from "@/contexts/SnackbarContext";
import { useReservationDocuments } from "@/features/bookings/hooks/useReservationDocuments";
import type { FindUserReservationDocumentsDocument } from "@/types/codegen";

type DownloadButtonProps = {
  doc: FindUserReservationDocumentsDocument;
};

const ReservationDocumentDownloadButton = ({ doc }: DownloadButtonProps) => {
  const intl = useIntl();
  const [isDownloading, setIsDownloading] = useState(false);

  const openLink = (doc: FindUserReservationDocumentsDocument) => {
    if (isDownloading) {
      return;
    }

    setIsDownloading(true);
    my?.downloadFile({
      url: doc.link,
      success({ apFilePath }) {
        my?.openDocument({
          filePath: apFilePath,
          fileType: "pdf",
        });
      },
      fail(res) {
        if (res.errorMessage) {
          showSnackbar({
            type: "error",
            content: res.errorMessage,
          });
        }
      },
      complete() {
        setIsDownloading(false);
      },
    });
  };

  if (isDownloading) {
    return (
      <div className="flex items-center justify-center gap-2 py-3">
        <CircularProgress size={14} />
        <div className="text-sm font-semibold text-text-primary">
          {intl.formatMessage({
            defaultMessage: "Voucher is being generated...",
            description:
              "Label informing user that hotel booking voucher is being generated",
          })}
        </div>
      </div>
    );
  }

  return (
    <Button
      fullWidth
      variant="outlined"
      // SuperQi has it's own api for displaying pdfs
      {...(my
        ? { onClick: () => openLink(doc) }
        : {
            href: doc.link,
            download: doc.name,
          })}
    >
      {intl.formatMessage(
        {
          defaultMessage: "Download {docName}",
          description: "Button for downloading hotel booking voucher",
        },
        { docName: doc.name },
      )}
    </Button>
  );
};

type Props = {
  reservationId: string;
};

export const ReservationDocumentList = ({ reservationId }: Props) => {
  const intl = useIntl();
  const { data } = useReservationDocuments(reservationId);

  if (!data?.length) {
    return (
      <div className="flex items-center justify-center gap-2">
        <CircularProgress size={20} />
        <span>
          {intl.formatMessage({
            defaultMessage: "Preparing tickets...",
            description:
              "Loading message shown while tickets are being issued after payment",
          })}
        </span>
      </div>
    );
  }

  return (
    <ul>
      {data?.map((doc) => (
        <li key={doc.link} className="not-last:mb-2">
          <ReservationDocumentDownloadButton doc={doc} />
        </li>
      ))}
    </ul>
  );
};
